import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { Sale, SaleState } from '../../types/sales';
import { SaleCreate, SaleUpdate } from './types';

// Async thunks
export const fetchSales = createAsyncThunk(
  'sales/fetchSales',
  async (_, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('sales')
        .select(`
          *,
          vendors (id, name),
          crops (id, name, variety)
        `)
        .eq('user_id', user.id)
        .order('sale_date', { ascending: false });

      if (error) throw error;
      return data as Sale[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addSale = createAsyncThunk(
  'sales/addSale',
  async (sale: SaleCreate, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('sales')
        .insert([{
          ...sale,
          user_id: user.id,
          quantity: Number(sale.quantity),
          unit_price: Number(sale.unit_price),
          purchase_price: sale.purchase_price ? Number(sale.purchase_price) : 0,
          notes: sale.notes?.trim() || null
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Sale;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateSale = createAsyncThunk(
  'sales/updateSale',
  async ({ id, ...updates }: SaleUpdate, thunkAPI) => {
    try {
      const { data, error } = await supabase
        .from('sales')
        .update({
          ...updates,
          quantity: Number(updates.quantity),
          unit_price: Number(updates.unit_price),
          purchase_price: updates.purchase_price ? Number(updates.purchase_price) : 0,
          notes: updates.notes?.trim() || null
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Sale;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteSale = createAsyncThunk(
  'sales/deleteSale',
  async (id: string, thunkAPI) => {
    try {
      const { error } = await supabase
        .from('sales')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const initialState: SaleState = {
  sales: [],
  loading: false,
  error: null,
};

const salesSlice = createSlice({
  name: 'sales',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = action.payload;
        state.error = null;
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addSale.fulfilled, (state, action) => {
        state.sales.unshift(action.payload);
      })
      .addCase(updateSale.fulfilled, (state, action) => {
        const index = state.sales.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sales[index] = action.payload;
        }
      })
      .addCase(deleteSale.fulfilled, (state, action) => {
        state.sales = state.sales.filter(s => s.id !== action.payload);
      });
  },
});

export default salesSlice.reducer;